/**
 * Automation types and adapters for React Query migration.
 * These provide ViewModel-like interfaces while using React Query for API operations.
 */

import * as Api from './sdk';

/**
 * Minimal interface for user session dependencies.
 * Avoids tight coupling to the full UserSessionContext class.
 */
interface IAutomationUserSession {
	webServiceHelper: Api.WebServiceHelper;
}

/**
 * Interface for ViewModel-like properties added to automation steps.
 */
interface ViewModelInterface<TStep> {
	model: TStep;
	setModel: (updates: Partial<TStep>) => void;
	update: (updates: Partial<TStep>) => void;
	isBusy: boolean;
	canEdit: boolean;
	showRequiredFieldError: boolean;
	hasAllRequiredInfo: boolean;
	toJs: () => TStep;
	impersonationContext?: Api.IImpersonationContext;
	templateId?: string;
	uuid: string;
}

/**
 * Options for constructing an AutomationTemplateEditorStep.
 */
interface AutomationTemplateEditorStepOptions {
	templateId?: string;
	impersonationContext?: Api.IImpersonationContext;
	canEdit?: boolean;
}

/**
 * Validates if an automation step has all required information based on its type.
 */
function validateAutomationStepInternal(step: Api.IAutomationStep | undefined): boolean {
	if (!step || !step._type) {
		return false;
	}

	const stepType = step._type as string;

	switch (stepType) {
		case Api.AutomationStepType.Email: {
			const emailStep = step as Api.IEmailAutomationStep;
			return !!emailStep.options?.subject && !!emailStep.content;
		}
		case Api.AutomationStepType.Texting: {
			const textingStep = step as Api.ITextingAutomationStep;
			return !!textingStep.content;
		}
		case Api.AutomationStepType.AddTag:
		case Api.AutomationStepType.RemoveTag: {
			const tagStep = step as Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep;
			return !!tagStep.tagName;
		}
		case Api.AutomationStepType.ActionItem: {
			const actionStep = step as Api.IActionItemAutomationStep;
			return !!actionStep.content;
		}
		case Api.AutomationStepType.HandwrittenCard:
			return !!step.content;
		case Api.AutomationStepType.HtmlNewsletter: {
			const newsletterStep = step as Api.IHtmlNewsletterAutomationStep;
			return !!newsletterStep.templateId || !!newsletterStep.content;
		}
		case Api.AutomationStepType.Opportunity:
			return true;
		case Api.AutomationStepType.Switch: {
			const switchStep = step as Api.ISwitchAutomationStep;
			return (
				!!switchStep.cases &&
				switchStep.cases.length > 0 &&
				switchStep.cases.some((c: Api.IAutomationStepCaseStatement) => !!c.automationTemplateId)
			);
		}
		case Api.AutomationStepType.NoAction:
			return true;
		default:
			return true;
	}
}

/**
 * Enhanced wrapper for automation steps used in the template editor.
 * Provides ViewModel-like interface while working with plain SDK interfaces.
 */
export class AutomationTemplateEditorStep<TStep extends Api.IAutomationStep = Api.IAutomationStep> {
	private _automationStep?: TStep;
	private _uuid: string;
	private _templateId?: string;
	private _impersonationContext?: Api.IImpersonationContext;
	private _showRequiredFieldError = false;
	private _isBusy = false;
	private _canEdit = true;

	/** Callback for React state updates when step data changes */
	public onStepChanged?: (step: TStep) => void;

	constructor(step?: TStep, options?: AutomationTemplateEditorStepOptions) {
		this._automationStep = step;
		this._uuid = step?.id || Math.random().toString(36).substring(7);
		this._templateId = options?.templateId;
		this._impersonationContext = options?.impersonationContext;
		this._canEdit = options?.canEdit ?? true;
	}

	// Core properties
	get uuid(): string {
		return this._uuid;
	}
	get id(): string | undefined {
		return this._automationStep?.id;
	}
	get templateId(): string | undefined {
		return this._templateId;
	}
	get impersonationContext(): Api.IImpersonationContext | undefined {
		return this._impersonationContext;
	}

	// State properties with getters/setters
	get isBusy(): boolean {
		return this._isBusy;
	}
	set isBusy(value: boolean) {
		this._isBusy = value;
	}
	get canEdit(): boolean {
		return this._canEdit;
	}
	set canEdit(value: boolean) {
		this._canEdit = value;
	}
	get showRequiredFieldError(): boolean {
		return this._showRequiredFieldError;
	}
	set showRequiredFieldError(value: boolean) {
		this._showRequiredFieldError = value;
	}

	// Computed property for validation
	get hasAllRequiredInfo(): boolean {
		return validateAutomationStepInternal(this._automationStep);
	}

	/**
	 * Enhanced automationStep getter that returns an object with ViewModel-like properties.
	 * Components can access step.automationStep.model, step.automationStep.setModel(), etc.
	 */
	get automationStep(): (TStep & ViewModelInterface<TStep>) | undefined {
		if (!this._automationStep) {
			return undefined;
		}

		const step = this._automationStep;

		// Return enhanced object with ViewModel-like properties merged in
		return {
			...step,
			model: step,
			setModel: (updates: Partial<TStep>) => this.setModel(updates),
			update: (updates: Partial<TStep>) => this.setModel(updates),
			isBusy: this._isBusy,
			canEdit: this._canEdit,
			showRequiredFieldError: this._showRequiredFieldError,
			hasAllRequiredInfo: this.hasAllRequiredInfo,
			toJs: () => ({ ...step }),
			impersonationContext: this._impersonationContext,
			templateId: this._templateId,
			uuid: this._uuid,
		} as TStep & ViewModelInterface<TStep>;
	}

	set automationStep(value: TStep | undefined) {
		this._automationStep = value;
		if (value) {
			this.onStepChanged?.(value);
		}
	}

	/**
	 * Updates the step data with partial updates.
	 */
	setModel(updates: Partial<TStep>): void {
		if (this._automationStep) {
			this._automationStep = { ...this._automationStep, ...updates };
			this.onStepChanged?.(this._automationStep);
		}
	}

	/**
	 * Returns a plain copy of the step data.
	 */
	toJs(): TStep | undefined {
		return this._automationStep ? { ...this._automationStep } : undefined;
	}
}

// Placeholder export to maintain import compatibility during migration
// Note: _template parameter accepts unknown since it's unused and callers pass various types
export function createAutomationStepViewModel<TStep extends Api.IAutomationStep = Api.IAutomationStep>(
	_template: unknown,
	stepData: TStep,
	_templateId: string
): TStep {
	// Simply return the step data without ViewModel wrapping
	return stepData;
}

// Type aliases for backward compatibility
export type AutomationStepViewModel<
	_TContext = unknown,
	TStep extends Api.IAutomationStep = Api.IAutomationStep,
> = TStep;
export type SwitchAutomationStepViewModel = Api.ISwitchAutomationStep;

/**
 * Union type that accepts either the AutomationTemplateViewModel class or raw IAutomationTemplate interface.
 * Use this for props that can accept either type.
 */
export type AutomationTemplateViewModelType = AutomationTemplateViewModel | Api.IAutomationTemplate;

/**
 * Minimal AutomationTemplateViewModel class that wraps IAutomationTemplate.
 * Provides ViewModel-like interface for components that instantiate this class.
 */
export class AutomationTemplateViewModel {
	private _template: Api.IAutomationTemplate;
	private _impersonationContext?: Api.IImpersonationContext;
	private _userSession: IAutomationUserSession;
	private _loadPromise: Promise<Api.IAutomationTemplate> | null = null;
	private _isLoading = false;

	constructor(userSession: IAutomationUserSession, template: Api.IAutomationTemplate) {
		this._userSession = userSession;
		this._template = template;
	}

	// Delegate all getters to _template
	get id() {
		return this._template?.id;
	}
	get name() {
		return this._template?.name;
	}
	get status() {
		return this._template?.status;
	}
	get scope() {
		return this._template?.scope;
	}
	get canEdit() {
		// Archived templates are read-only
		if (this._template?.status === Api.TemplateStatus.Archived) {
			return false;
		}
		// Industry-scoped templates (sample automations) are read-only for customers
		if (this._template?.scope === Api.TemplateScope.Industry) {
			return false;
		}
		return this._template?.canEdit ?? true;
	}
	get draft() {
		return this._template?.draft;
	}
	get published() {
		return this._template?.published;
	}
	get hasDraftVersion() {
		return !!this._template?.draft;
	}
	get hasPublishedVersion() {
		return !!this._template?.published;
	}
	get draftVersion() {
		return this._template?.draft;
	}
	get publishedVersion() {
		return this._template?.published;
	}
	get rootTemplate(): Api.IAutomationTemplate {
		return this._template;
	}
	get parentTemplate() {
		return this._template?.parentTemplate;
	}
	get impersonationContext() {
		return this._impersonationContext;
	}
	get creator() {
		return this._template?.creator;
	}
	get templateType() {
		return this._template?.templateType;
	}
	get cancelOnReply() {
		return this._template?.cancelOnReply;
	}
	get sendToHousehold() {
		return this._template?.sendToHousehold;
	}
	get allowUnsubscribed() {
		return this._template?.allowUnsubscribed;
	}
	get draftTriggerReference() {
		return this._template?.draft?.trigger;
	}
	get publishedTriggerReference() {
		return this._template?.published?.trigger;
	}
	get draftStepReferences() {
		return this._template?.draft?.steps;
	}
	get publishedStepReferences() {
		return this._template?.published?.steps;
	}
	get supportedStepModelTypes() {
		return this._template?.supportedStepModelTypes;
	}
	get stats() {
		return this._template?.stats;
	}
	get templateReview(): Api.TemplateReviewForCard | undefined {
		return (this._template as Api.IAutomationTemplate & { templateReview?: Api.TemplateReviewForCard })?.templateReview;
	}
	get isLoaded() {
		return !!this._template?.draft || !!this._template?.published;
	}
	get isBusy() {
		return this._isLoading;
	}

	// Methods
	impersonate(context: Api.IImpersonationContext): AutomationTemplateViewModel {
		this._impersonationContext = context;
		return this;
	}

	/**
	 * Loads the full template data from the API if only an ID is present.
	 */
	async load(): Promise<Api.IAutomationTemplate> {
		// If already loaded, return cached data
		if (this.isLoaded) {
			return this._template;
		}

		// If load is already in progress, return existing promise
		if (this._loadPromise) {
			return this._loadPromise;
		}

		// Need to fetch full template data
		if (this._template?.id && this._userSession?.webServiceHelper) {
			this._isLoading = true;

			const urlPath = this._impersonationContext
				? Api.ImpersonationBroker.composeApiUrl({
						urlPath: `AutomationTemplate/${encodeURIComponent(this._template.id)}`,
						impersonationContext: this._impersonationContext,
					})
				: `AutomationTemplate/${encodeURIComponent(this._template.id)}`;

			this._loadPromise = (
				this._userSession.webServiceHelper.callAsync(urlPath, 'GET') as Promise<Api.IAutomationTemplate>
			)
				.then((result: Api.IAutomationTemplate) => {
					this._template = result;
					this._isLoading = false;
					return result;
				})
				.catch((error: unknown) => {
					this._isLoading = false;
					this._loadPromise = null;
					throw error;
				});

			return this._loadPromise;
		}

		return this._template;
	}

	toJs(): Api.IAutomationTemplate {
		return { ...this._template };
	}

	/**
	 * Start the automation for a list of contacts.
	 */
	async startForContacts(request: Api.ICreateAutomationRequest): Promise<Api.IOperationResult<Api.IAutomation[]>> {
		const urlPath = this._impersonationContext
			? Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(this._template.id)}/startForContacts`,
					impersonationContext: this._impersonationContext,
				})
			: `AutomationTemplate/${encodeURIComponent(this._template.id)}/startForContacts`;

		return this._userSession.webServiceHelper.callAsync(urlPath, 'POST', request);
	}

	/**
	 * Create an automation for a single contact.
	 * @param contactOrRequest - Either a ContactViewModel, IContact, or an ICreateAutomationRequest
	 * @param preview - If true, preview only (used when first arg is a contact)
	 */
	async createAutomationForContact(
		contactOrRequest?: Api.ICreateAutomationRequest | Api.IContact | { id: string },
		preview?: boolean
	): Promise<Api.IOperationResult<Api.IAutomation>> {
		const urlPath = this._impersonationContext
			? Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(this._template.id)}/createAutomationForContact`,
					impersonationContext: this._impersonationContext,
				})
			: `AutomationTemplate/${encodeURIComponent(this._template.id)}/createAutomationForContact`;

		// Handle both old (contact, preview) and new (request) call patterns
		let request: Partial<Api.ICreateAutomationRequest> & { contactId?: string; preview?: boolean } = {};
		if (
			contactOrRequest &&
			'id' in contactOrRequest &&
			typeof contactOrRequest.id === 'string' &&
			!('contactIds' in contactOrRequest)
		) {
			// Old pattern: first arg is a ContactViewModel or IContact
			request = {
				contactId: contactOrRequest.id,
				preview: preview ?? false,
			};
		} else if (contactOrRequest) {
			// New pattern: first arg is a request object
			request = contactOrRequest as Api.ICreateAutomationRequest;
		}

		return this._userSession.webServiceHelper.callAsync(urlPath, 'POST', request);
	}

	/**
	 * Create automations for a resource selector.
	 */
	async createAutomationForResouceSelector(
		request: Api.ICreateAutomationForResourceSelectorRequest
	): Promise<Api.IOperationResult<Api.ICreateAutomationsResult>> {
		const urlPath = this._impersonationContext
			? Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(this._template.id)}/createAutomationForResouceSelector`,
					impersonationContext: this._impersonationContext,
				})
			: `AutomationTemplate/${encodeURIComponent(this._template.id)}/createAutomationForResouceSelector`;

		return this._userSession.webServiceHelper.callAsync(urlPath, 'POST', request);
	}

	/**
	 * Preview starting the automation for contacts without actually starting it.
	 */
	async previewStartForContacts(
		request: Api.ICreateAutomationRequest | Api.ICreateAutomationRequest[]
	): Promise<Api.IOperationResult<Api.IPreviewCreateAutomationResult>> {
		// Handle both single request and array of requests for backwards compatibility
		const requestBody = Array.isArray(request) ? request[0] : request;

		const urlPath = this._impersonationContext
			? Api.ImpersonationBroker.composeApiUrl({
					urlPath: `Automation/${encodeURIComponent(this._template.id)}/Contact/preview`,
					impersonationContext: this._impersonationContext,
				})
			: `Automation/${encodeURIComponent(this._template.id)}/Contact/preview`;

		return this._userSession.webServiceHelper.callAsync(urlPath, 'POST', requestBody);
	}

	/**
	 * Add a trigger to the automation template.
	 */
	async addTrigger(trigger: Api.IAutomationTrigger): Promise<Api.IOperationResult<Api.IAutomationTemplate>> {
		const urlPath = this._impersonationContext
			? Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(this._template.id)}/trigger`,
					impersonationContext: this._impersonationContext,
				})
			: `AutomationTemplate/${encodeURIComponent(this._template.id)}/trigger`;

		return this._userSession.webServiceHelper.callAsync(urlPath, 'POST', trigger);
	}
}

// TEMPORARY: Declaration merging to add ViewModel-like properties to SDK interfaces
// This allows components that were built for ViewModels to continue working
// while we gradually migrate them to work directly with interfaces.
// TODO: Remove these as components are refactored to not depend on them.

declare module './sdk' {
	interface IEmailAutomationStep {
		model?: Api.IEmailAutomationStep;
		setModel?: (model: Partial<Api.IEmailAutomationStep>) => void;
		canEdit?: boolean;
		showRequiredFieldError?: boolean;
		uuid?: string;
		templateId?: string;
		impersonationContext?: Api.IImpersonationContext;
		isBusy?: boolean;
		sendToSender?: boolean;
		hasAllRequiredInfo?: boolean;
		validate?: () => boolean;
		update?: (updates: Partial<Api.IEmailAutomationStep>) => void;
		toJs?: () => Api.IEmailAutomationStep;
	}

	interface ITextingAutomationStep {
		model?: Api.ITextingAutomationStep;
		setModel?: (model: Partial<Api.ITextingAutomationStep>) => void;
		canEdit?: boolean;
		showRequiredFieldError?: boolean;
		uuid?: string;
		templateId?: string;
		impersonationContext?: Api.IImpersonationContext;
		isBusy?: boolean;
		hasAllRequiredInfo?: boolean;
		validate?: () => boolean;
		update?: (updates: Partial<Api.ITextingAutomationStep>) => void;
		toJs?: () => Api.ITextingAutomationStep;
	}

	interface ISwitchAutomationStep {
		model?: Api.ISwitchAutomationStep;
		setModel?: (model: Partial<Api.ISwitchAutomationStep>) => void;
		canEdit?: boolean;
		showRequiredFieldError?: boolean;
		uuid?: string;
		templateId?: string;
		impersonationContext?: Api.IImpersonationContext;
		isBusy?: boolean;
		hasAllRequiredInfo?: boolean;
		validate?: () => boolean;
		update?: (updates: Partial<Api.ISwitchAutomationStep>) => void;
		toJs?: () => Api.ISwitchAutomationStep;
		getTemplateForCase?: (caseStatement: Api.IAutomationStepCaseStatement) => Api.IAutomationTemplate;
	}

	interface ITagAutomationStep {
		model?: Api.ITagAutomationStep;
		setModel?: (model: Partial<Api.ITagAutomationStep>) => void;
		canEdit?: boolean;
		showRequiredFieldError?: boolean;
		uuid?: string;
		templateId?: string;
		impersonationContext?: Api.IImpersonationContext;
		isBusy?: boolean;
		hasAllRequiredInfo?: boolean;
		validate?: () => boolean;
		update?: (updates: Partial<Api.ITagAutomationStep>) => void;
		toJs?: () => Api.ITagAutomationStep;
	}

	interface IActionItemAutomationStep {
		model?: Api.IActionItemAutomationStep;
		setModel?: (model: Partial<Api.IActionItemAutomationStep>) => void;
		canEdit?: boolean;
		showRequiredFieldError?: boolean;
		uuid?: string;
		templateId?: string;
		impersonationContext?: Api.IImpersonationContext;
		isBusy?: boolean;
		hasAllRequiredInfo?: boolean;
		validate?: () => boolean;
		update?: (updates: Partial<Api.IActionItemAutomationStep>) => void;
		toJs?: () => Api.IActionItemAutomationStep;
	}

	interface IAutomationStep {
		model?: Api.IAutomationStep;
		// Note: setModel and update use Record<string, unknown> to avoid contravariance issues with child interfaces
		setModel?: (model: Record<string, unknown>) => void;
		canEdit?: boolean;
		showRequiredFieldError?: boolean;
		uuid?: string;
		templateId?: string;
		impersonationContext?: Api.IImpersonationContext;
		isBusy?: boolean;
		hasAllRequiredInfo?: boolean;
		validate?: () => boolean;
		update?: (updates: Record<string, unknown>) => void;
		toJs?: () => Api.IAutomationStep;
		automationTemplate?: Api.IAutomationTemplate;
	}

	interface IAutomationTrigger {
		isUserCreated?: boolean;
	}

	interface IAutomationTemplate {
		isImpersonating?: boolean;
		draftVersion?: Api.IAutomationTemplateVersion;
		publishedVersion?: Api.IAutomationTemplateVersion;
		hasPublishedVersion?: boolean;
		hasDraftVersion?: boolean;
		publishedStepReferences?: Api.IAutomationStepReference[];
		draftTriggerReference?: Api.IAutomationTrigger;
		publishedTriggerReference?: Api.IAutomationTrigger;
		parentTemplate?: Api.IAutomationTemplate;
		isBusy?: boolean;
		rootTemplate?: Api.IAutomationTemplate;
		model?: Api.IAutomationTemplate;
		impersonationContext?: Api.IImpersonationContext;
		canEdit?: boolean;
		supportedStepModelTypes?: string[];
		isLoaded?: boolean;
		stats?: Api.IAutomationTemplateStats;
		templateReview?: Api.TemplateReviewForCard;
		createAutomationForResouceSelector?: (
			request: Api.ICreateAutomationForResourceSelectorRequest
		) => Promise<Api.IOperationResult<Api.ICreateAutomationsResult>>;
		startForContacts?: (request: Api.ICreateAutomationRequest) => Promise<Api.IOperationResult<Api.IAutomation[]>>;
		createAutomationForContact?: (
			contactOrRequest?: Api.ICreateAutomationRequest | Api.IContact | { id: string },
			preview?: boolean
		) => Promise<Api.IOperationResult<Api.IAutomation>>;
		previewStartForContacts?: (
			request: Api.ICreateAutomationRequest
		) => Promise<Api.IOperationResult<Api.IPreviewCreateAutomationResult>>;
		addTrigger?: (trigger: Api.IAutomationTrigger) => Promise<Api.IOperationResult<Api.IAutomationTemplate>>;
		toJs?: () => Api.IAutomationTemplate;
		impersonate?: (context: Api.IImpersonationContext) => AutomationTemplateViewModel;
		load?: () => Promise<Api.IAutomationTemplate>;
	}
}
